import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Edit, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { US_STATES } from "@/lib/usStates";
import { EMPLOYMENT_TYPES, getLabel } from "@shared/constants/employment";
import { useOrgOptions } from "./useOrgOptions";

type EligibilityGroup = {
  id: number;
  clientId: number;
  planId: number;
  name: string | null;
  priority: number;
  minHoursPerWeek: number | null;
  allowedStates: string[] | null;
  allowedLocationIds: number[] | null;
  classIds: number[] | null;
  employmentTypes: string[] | null;
  contributionRuleSetId: number;
};

type ResolutionEntry = { groupId: number; ruleSetId: number } | null;
type ResolutionMap = Record<number, ResolutionEntry>;
type UnmatchedMap = Record<number, { reason: string; enrolled: boolean }>;

type GroupForm = {
  name: string;
  priority: string;
  allowedStates: string[];
  allowedLocationIds: string[];
  classIds: string[];
  employmentTypes: string[];
  minHoursPerWeek: string;
};

const EMPTY_FORM: GroupForm = {
  name: "",
  priority: "0",
  allowedStates: [],
  allowedLocationIds: [],
  classIds: [],
  employmentTypes: [],
  minHoursPerWeek: "",
};

const STATE_OPTIONS = US_STATES.map((s) => ({ value: s.code, label: `${s.code} — ${s.name}` }));
const EMPLOYMENT_OPTIONS = EMPLOYMENT_TYPES.map((e) => ({ value: e.value, label: e.label }));

function idsFromStrings(values: string[]): number[] | null {
  const ids = values
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n));
  return ids.length > 0 ? ids : null;
}

function formToPayload(f: GroupForm) {
  return {
    name: f.name.trim() || null,
    priority: Number(f.priority) || 0,
    allowedStates: f.allowedStates.length > 0 ? f.allowedStates : null,
    allowedLocationIds: idsFromStrings(f.allowedLocationIds),
    classIds: idsFromStrings(f.classIds),
    employmentTypes: f.employmentTypes.length > 0 ? f.employmentTypes : null,
    minHoursPerWeek: f.minHoursPerWeek.trim()
      ? Number(f.minHoursPerWeek)
      : null,
  };
}

function groupToForm(g: EligibilityGroup): GroupForm {
  return {
    name: g.name ?? "",
    priority: String(g.priority ?? 0),
    allowedStates: g.allowedStates ?? [],
    allowedLocationIds: (g.allowedLocationIds ?? []).map(String),
    classIds: (g.classIds ?? []).map(String),
    employmentTypes: g.employmentTypes ?? [],
    minHoursPerWeek: g.minHoursPerWeek != null ? String(g.minHoursPerWeek) : "",
  };
}

const typeLabels = (types: string[]) =>
  types.map((t) => getLabel(EMPLOYMENT_TYPES, t)).join(", ");

// Groups without their own employment types inherit the plan-level ones
// (server resolver, Q1 decision (a)) — the label must say so, or a group
// that filters by type reads as "matches everyone".
function filterSummary(
  g: EligibilityGroup,
  planEmploymentTypes: string[] | null,
): string {
  const parts: string[] = [];
  if (g.allowedStates?.length) parts.push(`states: ${g.allowedStates.join(", ")}`);
  if (g.allowedLocationIds?.length) parts.push(`locations: ${g.allowedLocationIds.length}`);
  if (g.classIds?.length) parts.push(`classes: ${g.classIds.length}`);
  if (g.employmentTypes?.length) parts.push(`types: ${typeLabels(g.employmentTypes)}`);
  else if (planEmploymentTypes?.length)
    parts.push(`types: ${typeLabels(planEmploymentTypes)} (plan default)`);
  if (g.minHoursPerWeek != null) parts.push(`≥${g.minHoursPerWeek} hrs/wk`);
  return parts.length > 0 ? parts.join(" · ") : "No filters (matches everyone)";
}

export default function EligibilityGroupsPanel({
  clientId,
  planId,
  activeRuleSetId,
  activeEligibilityGroupId,
  onActiveRuleSetChange,
  planEmploymentTypes = null,
}: {
  clientId: number;
  planId: number;
  // Plan-level employment types; groups with none of their own inherit them.
  planEmploymentTypes?: string[] | null;
  activeRuleSetId: number | null;
  // The group whose eligibility form is currently shown below the panel —
  // highlighted so the broker sees "this is the one I'm editing".
  activeEligibilityGroupId?: number | null;
  onActiveRuleSetChange: (ruleSetId: number | null, groupId: number | null) => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { locationOptions, classOptions } = useOrgOptions(clientId);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<EligibilityGroup | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<EligibilityGroup | null>(null);
  const [form, setForm] = useState<GroupForm>(EMPTY_FORM);
  // "Copy filters from..." picker — only relevant while creating a new group.
  // null = blank/starter values. Otherwise the picked group's filters are
  // copied into the form (broker tweaks differences and saves).
  const [copyFromGroupId, setCopyFromGroupId] = useState<string>("");

  // Load groups
  const { data: groups = [], isLoading } = useQuery<EligibilityGroup[]>({
    queryKey: ["/api/eligibility-groups", clientId, planId],
    queryFn: async () => {
      const res = await fetch(
        `/api/eligibility-groups?clientId=${clientId}&planId=${planId}`,
      );
      if (!res.ok) throw new Error("Failed to load groups");
      return res.json();
    },
  });

  // Load client employees (for live preview via resolve-bulk)
  const { data: employees = [] } = useQuery<any[]>({
    queryKey: ["/api/employees", { clientId }],
    queryFn: async () => {
      const res = await fetch(`/api/employees?clientId=${clientId}`);
      if (!res.ok) return [];
      const json = await res.json();
      return Array.isArray(json) ? json : json.employees || [];
    },
  });

  // Resolve all employees → groups
  const employeeIds = useMemo(
    () =>
      (employees || [])
        .map((e: any) => Number(e?.id))
        .filter((n: number) => Number.isFinite(n)),
    [employees],
  );

  // Hash the filter-relevant fields of every group so the preview refetches
  // when a group's filters change — not just when groups are added/removed.
  // Keying on counts alone left member counts stale after an edit.
  const groupsSignature = useMemo(
    () =>
      JSON.stringify(
        groups.map((g) => [
          g.id,
          g.priority,
          g.minHoursPerWeek,
          g.allowedStates,
          g.allowedLocationIds,
          g.classIds,
          g.employmentTypes,
        ]),
      ),
    [groups],
  );

  const { data: resolution } = useQuery<{ resolutions: ResolutionMap; unmatched?: UnmatchedMap }>({
    queryKey: ["eligibility-groups-resolve", clientId, planId, employeeIds.length, groupsSignature],
    enabled: employeeIds.length > 0 && groups.length > 0,
    queryFn: async () => {
      const res = await apiRequest("POST", "/api/eligibility-groups/resolve-bulk", {
        clientId,
        planId,
        employeeIds,
        includeReasons: true,
      });
      const data = await res.json();
      // Endpoint shape: either { resolutions: {...} } or raw {id: entry}
      if (data?.resolutions) return data;
      return { resolutions: data ?? {} };
    },
  });

  const resolutions: ResolutionMap = resolution?.resolutions ?? {};
  const memberCountByGroup = useMemo(() => {
    const counts: Record<number, number> = {};
    for (const groupId of Object.keys(resolutions)) {
      const entry = resolutions[Number(groupId)];
      if (entry) counts[entry.groupId] = (counts[entry.groupId] ?? 0) + 1;
    }
    return counts;
  }, [resolutions]);

  const unmatchedCount = useMemo(() => {
    if (employeeIds.length === 0) return 0;
    let n = 0;
    for (const id of employeeIds) {
      if (!resolutions[id]) n++;
    }
    return n;
  }, [employeeIds, resolutions]);

  // Unmatched employees with the server's reason, enrolled ones first —
  // those hold coverage on this plan yet count toward no group.
  const unmatchedRows = useMemo(() => {
    const unmatched = resolution?.unmatched ?? {};
    return (employees || [])
      .filter((e: any) => unmatched[Number(e?.id)])
      .map((e: any) => ({
        id: Number(e.id),
        name: [e.firstName, e.lastName].filter(Boolean).join(" ") || `Employee ${e.id}`,
        ...unmatched[Number(e.id)],
      }))
      .sort((a, b) => Number(b.enrolled) - Number(a.enrolled) || a.name.localeCompare(b.name));
  }, [employees, resolution]);
  const enrolledUnmatchedCount = unmatchedRows.filter((r) => r.enrolled).length;

  // The primary group is the lowest-priority (default) row. Its identity +
  // filters are also editable inline via the existing "Eligibility Requirements"
  // card below — both surfaces PATCH the same row.
  const primaryGroup = groups[0] ?? null;

  // Auto-select first group on load if none active
  useEffect(() => {
    if (activeRuleSetId == null && groups.length > 0) {
      onActiveRuleSetChange(groups[0].contributionRuleSetId, groups[0].id);
    }
  }, [activeRuleSetId, groups, onActiveRuleSetChange]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = { clientId, planId, ...formToPayload(form) };
      const res = await apiRequest("POST", "/api/eligibility-groups", payload);
      return res.json();
    },
    onSuccess: (created: EligibilityGroup) => {
      toast({ title: "Group created", variant: "success" });
      setIsAddOpen(false);
      setForm(EMPTY_FORM);
      qc.invalidateQueries({ queryKey: ["/api/eligibility-groups", clientId, planId] });
      onActiveRuleSetChange(created.contributionRuleSetId, created.id);
    },
    onError: (err: any) => {
      toast({ title: "Create failed", description: err?.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingGroup) return;
      const res = await apiRequest(
        "PATCH",
        `/api/eligibility-groups/${editingGroup.id}`,
        formToPayload(form),
      );
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Group updated", variant: "success" });
      setEditingGroup(null);
      setForm(EMPTY_FORM);
      qc.invalidateQueries({ queryKey: ["/api/eligibility-groups", clientId, planId] });
    },
    onError: (err: any) => {
      toast({ title: "Update failed", description: err?.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!deletingGroup) return;
      const res = await fetch(`/api/eligibility-groups/${deletingGroup.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || `Delete failed (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Group deleted", variant: "success" });
      // If we just deleted the active group, switch to whatever's left.
      if (deletingGroup && activeRuleSetId === deletingGroup.contributionRuleSetId) {
        const remaining = groups.filter((g) => g.id !== deletingGroup.id);
        if (remaining.length > 0) {
          onActiveRuleSetChange(remaining[0].contributionRuleSetId, remaining[0].id);
        } else {
          onActiveRuleSetChange(null, null);
        }
      }
      setDeletingGroup(null);
      qc.invalidateQueries({ queryKey: ["/api/eligibility-groups", clientId, planId] });
    },
    onError: (err: any) => {
      toast({ title: "Delete failed", description: err?.message, variant: "destructive" });
    },
  });

  const openEdit = (g: EligibilityGroup) => {
    setForm(groupToForm(g));
    setEditingGroup(g);
  };

  const dialogOpen = isAddOpen || editingGroup !== null;
  const closeDialog = () => {
    setIsAddOpen(false);
    setEditingGroup(null);
    setForm(EMPTY_FORM);
    setCopyFromGroupId("");
  };

  // Radix Select forbids an empty-string value, so we use a sentinel for the
  // "Start from blank" option and translate at the boundary.
  const COPY_FROM_BLANK = "__blank__";

  // When the broker picks a "Copy from" source while adding, clone its filters
  // into the form. Name and priority are NOT copied (those should be group-
  // specific - otherwise you get two "Default group" rows or a priority clash).
  const applyCopyFrom = (groupId: string) => {
    setCopyFromGroupId(groupId);
    if (!groupId || groupId === COPY_FROM_BLANK) {
      setForm(EMPTY_FORM);
      return;
    }
    const src = groups.find((g) => String(g.id) === groupId);
    if (!src) return;
    const srcForm = groupToForm(src);
    setForm({
      ...srcForm,
      name: form.name, // keep whatever broker typed
      priority: form.priority,
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle>Eligibility Groups</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Split a plan into multiple groups, each with its own filters and contributions —
            e.g. CA employees at 100% of the CA base plan, OOS at 100% of the national plan.
            Higher-priority groups win on overlap.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            // Default-clone from the primary (lowest-priority) group if any
            // exist, so the broker only tweaks differences instead of typing
            // every filter from scratch.
            const primary = groups[0];
            if (primary) {
              const src = groupToForm(primary);
              setForm({ ...src, name: "", priority: String((primary.priority ?? 0) + 1) });
              setCopyFromGroupId(String(primary.id));
            } else {
              setForm(EMPTY_FORM);
              setCopyFromGroupId("");
            }
            setIsAddOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-1" /> Add group
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {unmatchedCount > 0 && (
          <div
            className="rounded border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40 p-3 text-sm flex items-start gap-2"
            data-testid="eligibility-groups-unmatched"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-700 dark:text-amber-300" />
            <div className="min-w-0 flex-1">
              {enrolledUnmatchedCount > 0 && (
                <p className="font-medium">
                  {enrolledUnmatchedCount} enrolled employee{enrolledUnmatchedCount === 1 ? " is" : "s are"} not
                  in any group.
                </p>
              )}
              <p>
                <strong>{unmatchedCount}</strong> employee{unmatchedCount === 1 ? "" : "s"} match no group on this plan.
                They'll be treated as ineligible — add a group with broader filters if that's not intentional.
              </p>
              {unmatchedRows.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {unmatchedRows.map((r) => (
                    <li
                      key={r.id}
                      className="flex flex-wrap items-center gap-x-2"
                      data-testid={`eligibility-unmatched-${r.id}`}
                    >
                      <span className="font-medium">{r.name}</span>
                      {r.enrolled && (
                        <Badge variant="outline" className="text-xs">Enrolled</Badge>
                      )}
                      <span className="text-muted-foreground">— {r.reason}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No groups yet. Click "Add group" to create the first one.
          </p>
        ) : (
          <div className="space-y-2">
            {groups.map((g) => {
              const isActive =
                activeEligibilityGroupId != null
                  ? activeEligibilityGroupId === g.id
                  : activeRuleSetId === g.contributionRuleSetId;
              const count = memberCountByGroup[g.id] ?? 0;
              return (
                <div
                  key={g.id}
                  data-testid={`eligibility-group-${g.id}`}
                  className={
                    "rounded border p-3 flex items-start justify-between gap-3 cursor-pointer transition-colors " +
                    (isActive
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-950/40"
                      : "hover:bg-gray-50 dark:hover:bg-gray-900")
                  }
                  onClick={() => onActiveRuleSetChange(g.contributionRuleSetId, g.id)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {isActive && (
                        <CheckCircle2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      )}
                      <span className="font-medium">
                        {g.name || (g.priority === 0 ? "Default group" : `Group ${g.id}`)}
                      </span>
                      {g.id === primaryGroup?.id && (
                        <Badge className="text-xs bg-amber-100 text-amber-800 hover:bg-amber-100">
                          Primary
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs">priority {g.priority}</Badge>
                      <Badge
                        variant="outline"
                        className="text-xs"
                        data-testid="eligibility-group-member-count"
                      >
                        {count} member{count === 1 ? "" : "s"}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        rule set #{g.contributionRuleSetId}
                      </Badge>
                    </div>
                    <p
                      className="text-xs text-muted-foreground mt-1"
                      data-testid="eligibility-group-filter-summary"
                    >
                      {filterSummary(g, planEmploymentTypes)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(g)}
                      title="Edit"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDeletingGroup(g)}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingGroup ? "Edit group" : "Add eligibility group"}</DialogTitle>
            <DialogDescription>
              Filter dimensions are AND'd. Higher priority wins on overlap. Leave a dimension blank to ignore it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {!editingGroup && groups.length > 0 && (
              <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 border p-2">
                <Label className="text-xs">Copy filters from</Label>
                <Select value={copyFromGroupId || COPY_FROM_BLANK} onValueChange={applyCopyFrom}>
                  <SelectTrigger>
                    <SelectValue placeholder="Start from blank" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={COPY_FROM_BLANK}>Start from blank</SelectItem>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={String(g.id)}>
                        {g.name || (g.priority === 0 ? "Primary group" : `Group ${g.id}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Filters are copied in; tweak what's different (e.g. swap states or class) and give it a unique name.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. California office"
                />
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <Input
                  type="number"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Allowed states</Label>
              <MultiSelect
                options={STATE_OPTIONS}
                selected={form.allowedStates}
                onChange={(v) => setForm({ ...form, allowedStates: v })}
                placeholder="Any state"
              />
            </div>

            <div>
              <Label className="text-xs">Employment types</Label>
              <MultiSelect
                options={EMPLOYMENT_OPTIONS}
                selected={form.employmentTypes}
                onChange={(v) => setForm({ ...form, employmentTypes: v })}
                placeholder={
                  planEmploymentTypes?.length
                    ? `Plan default: ${typeLabels(planEmploymentTypes)}`
                    : "Any employment type"
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Locations</Label>
                <MultiSelect
                  options={locationOptions}
                  selected={form.allowedLocationIds}
                  onChange={(v) => setForm({ ...form, allowedLocationIds: v })}
                  placeholder={locationOptions.length === 0 ? "No locations defined" : "Any location"}
                  disabled={locationOptions.length === 0}
                />
              </div>
              <div>
                <Label className="text-xs">Job classes</Label>
                <MultiSelect
                  options={classOptions}
                  selected={form.classIds}
                  onChange={(v) => setForm({ ...form, classIds: v })}
                  placeholder={classOptions.length === 0 ? "No classes defined" : "Any class"}
                  disabled={classOptions.length === 0}
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Min hours per week</Label>
              <Input
                type="number"
                value={form.minHoursPerWeek}
                onChange={(e) => setForm({ ...form, minHoursPerWeek: e.target.value })}
                placeholder="e.g. 30"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button
              onClick={() =>
                editingGroup ? updateMutation.mutate() : createMutation.mutate()
              }
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? "Saving…"
                : editingGroup
                  ? "Save"
                  : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={deletingGroup !== null}
        onOpenChange={(o) => !o && setDeletingGroup(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this group?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingGroup?.name || "This group"} will be removed along with its contribution rule set.
              Existing enrollments that snapshotted this group will block deletion.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
